import React, { useState, useEffect, useMemo } from 'react';
import { 
  Input, 
  Button, 
  Card, 
  Tag, 
  Space, 
  Pagination, 
  message,
  Modal,
  Table,
  Image,
  Switch
} from 'antd';
import { 
  SearchOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { productService } from '../services/productService';
import type { Product } from '../types/product';
import {
  getCategoryLabel,
  getChildCategoryLabel,
  pickChildCategoryFields,
} from '../constants/productCategory';
import { useLanguage } from '../contexts/LanguageContext';

const { Search } = Input;

const ProductList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState('');

  // 获取商品列表
  const fetchProducts = async (page = 1, size = 20, search = '') => {
    setLoading(true);
    try {
      const response = await productService.getProductList({
        page,
        size,
        search
      });
      setProducts(response.data.records || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      message.error(t('msgFetchProductListFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(current, pageSize, searchText);
  }, [current, pageSize]);

  // 搜索
  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrent(1);
    fetchProducts(1, pageSize, value);
  };

  // 新增商品
  const handleAdd = () => {
    navigate('/products/edit');
  };

  // 编辑商品
  const handleEdit = (id: string) => {
    navigate(`/products/edit/${id}`);
  };

  // 删除商品
  const handleDelete = (id: string) => {
    Modal.confirm({
      title: t('confirmDeleteTitle'),
      content: t('confirmDeleteProduct'),
      onOk: async () => {
        try {
          await productService.deleteProduct(id);
          message.success(t('msgDeleteSuccess'));
          fetchProducts(current, pageSize, searchText);
        } catch (error) {
          message.error(t('msgDeleteFailed'));
        }
      },
    });
  };

  // 上架/下架商品
  const handleToggleStatus = (id: string, newStatus: number) => {
    const isOnline = newStatus === 1;
    Modal.confirm({
      title: isOnline ? t('confirmOnlineTitle') : t('confirmOfflineTitle'),
      content: isOnline ? t('confirmOnlineProduct') : t('confirmOfflineProduct'),
      onOk: async () => {
        try {
          if (isOnline) {
            await productService.onlineProduct(id);
            message.success(t('msgOnlineSuccess'));
          } else {
            await productService.offlineProduct(id);
            message.success(t('msgOfflineSuccess'));
          }
          fetchProducts(current, pageSize, searchText);
        } catch (error) {
          message.error(t('msgOperationFailed'));
        }
      },
    });
  };

  // 批量删除
  const handleBatchDelete = (ids: string[]) => {
    Modal.confirm({
      title: t('confirmBatchDeleteTitle'),
      content: t('confirmBatchDeleteProducts', { count: ids.length }),
      onOk: async () => {
        try {
          await productService.batchDeleteProducts(ids);
          message.success(t('msgBatchDeleteSuccess'));
          fetchProducts(current, pageSize, searchText);
        } catch (error) {
          message.error(t('msgBatchDeleteFailed'));
        }
      },
    });
  };

  // 表格列配置
  const columns = useMemo(() => [
    {
      title: t('fieldProductId'),
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (id: string) => (
        <span style={{ 
          fontFamily: 'monospace', 
          fontSize: '12px',
          color: '#666',
          backgroundColor: '#f5f5f5',
          padding: '2px 6px',
          borderRadius: '4px'
        }}>
          {id}
        </span>
      ),
    },
    {
      title: t('fieldProductInfo'),
      key: 'product',
      width: 200,
      ellipsis: true,
      render: (record: Product) => (
        <div style={{ display: 'flex', alignItems: 'center', maxWidth: 200 }}>
          <Image
            width={44}
            height={44}
            src={record.logoUrl || '/api/placeholder/60/60'}
            alt={record.name}
            style={{
              borderRadius: '6px',
              objectFit: 'cover',
              backgroundColor: '#f5f5f5',
              flexShrink: 0,
            }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
          />
          <div style={{ marginLeft: 8, minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                marginBottom: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {record.name}
            </div>
            <div
              style={{
                fontSize: 12,
                color: '#8c8c8c',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {record.remark || t('emptyNoDescription')}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: t('fieldType'),
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (category: string, record: Product) => (
        <Tag color="orange">{getCategoryLabel(category, record.categoryDesc)}</Tag>
      ),
    },
    {
      title: t('fieldSubType'),
      key: 'childCategory',
      width: 100,
      render: (_: unknown, record: Product) => {
        const { code, desc } = pickChildCategoryFields(record);
        return <Tag color="blue">{getChildCategoryLabel(code, desc)}</Tag>;
      },
    },
    {
      title: t('fieldStatus'),
      dataIndex: 'isShow',
      key: 'isShow',
      width: 100,
      render: (isShow: number) => (
        <Tag color={isShow === 1 ? 'green' : 'red'}>
          {isShow === 1 ? t('statusOnline') : t('statusOffline')}
        </Tag>
      ),
    },
    {
      title: t('fieldPrice'),
      key: 'price',
      width: 120,
      render: (record: Product) => (
        <div>
          <div style={{ fontWeight: 500, color: '#1890ff' }}>
            ¥{record.discountPrice || record.price}
          </div>
          {record.discountPrice && record.discountPrice !== record.price && (
            <div style={{ fontSize: 12, color: '#8c8c8c', textDecoration: 'line-through' }}>
              ¥{record.price}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('fieldLogistics'),
      key: 'delivery',
      width: 90,
      render: (record: Product) =>
        record.deliveryFee !== undefined && record.deliveryFee !== null
          ? `${record.deliveryFee} zł`
          : '--',
    },
    {
      title: t('fieldCreateTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 170,
      render: (date: string) => date || '--',
    },
    {
      title: t('fieldActions'),
      key: 'action',
      width: 200,
      render: (record: Product) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/products/detail/${record.id}`)}
          >
            {t('actionView')}
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record.id)}
          >
            {t('actionEdit')}
          </Button>
          <Switch
            checked={record.isShow === 1}
            onChange={(checked) => handleToggleStatus(record.id, checked ? 1 : 0)}
            checkedChildren={t('statusOnline')}
            unCheckedChildren={t('statusOffline')}
            size="small"
            style={{
              backgroundColor: record.isShow === 1 ? '#52c41a' : '#d9d9d9',
              minWidth: '60px'
            }}
          />
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            {t('actionDelete')}
          </Button>
        </Space>
      ),
    },
  ], [t, navigate]);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{t('productManagement')}</h1>
      </div>

      <Card className="content-card">
        <div className="page-actions">
          <Search
            placeholder={t('searchProductName')}
            allowClear
            enterButton={<SearchOutlined />}
            size="middle"
            onSearch={handleSearch}
            style={{ width: 300 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            {t('addProduct')}
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={products}
          loading={loading}
          rowKey="id"
          pagination={false}
          scroll={{ x: 800 }}
        />

        <div className="pagination-wrapper">
          <Pagination
            current={current}
            total={total}
            pageSize={pageSize}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) => 
              `第 ${range[0]}-${range[1]} 条/总共 ${total} 条`
            }
            onChange={(page, size) => {
              setCurrent(page);
              setPageSize(size || 20);
            }}
            onShowSizeChange={(current, size) => {
              setCurrent(1);
              setPageSize(size);
            }}
          />
        </div>
      </Card>
    </div>
  );
};

export default ProductList;
